import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, Film, ImageIcon } from "lucide-react";
import { ACADEMY_CATEGORIES, mediaUrl, uploadVideo, readVideoDuration } from "@/lib/academy";

const EMPTY = { title: "", description: "", category: "Getting Started", duration: 0, thumbnail_url: null, video_url: null, transcript: "", featured: false, status: "active" };

const MediaPicker = ({ label, icon: Icon, accept, busy, progress, onPick, testid, children }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <div className="rounded-lg border border-dashed border-border p-3 space-y-2">
      {children}
      <label className={`inline-flex items-center gap-2 text-sm font-medium cursor-pointer text-primary ${busy ? "pointer-events-none opacity-60" : ""}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}<Icon className="w-4 h-4" />{busy ? "Uploading…" : "Choose file"}
        <input type="file" accept={accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onPick(f); }} data-testid={testid} />
      </label>
      {progress != null && <div className="space-y-1"><Progress value={progress} className="h-1.5" /><div className="text-xs text-muted-foreground tabular-nums" data-testid={`${testid}-progress`}>{progress}% uploaded</div></div>}
    </div>
  </div>
);

export const AcademyLessonDialog = ({ lesson, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [vidPct, setVidPct] = useState(null);
  const [vidMsg, setVidMsg] = useState("");
  const [thumbBusy, setThumbBusy] = useState(false);
  useEffect(() => { if (open) { setForm(lesson ? { ...EMPTY, ...lesson } : EMPTY); setVidPct(null); setVidMsg(""); } }, [open, lesson]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickVideo = async (file) => {
    if (!file.type.startsWith("video/")) return toast.error("Choose a video file (MP4, WebM or MOV)");
    try {
      setVidPct(0);
      const secs = await readVideoDuration(file);
      const url = await uploadVideo(file, setVidPct);
      setForm((f) => ({ ...f, video_url: url, duration: secs || f.duration }));
      setVidPct(null);
      setVidMsg(secs ? "Video attached — duration filled from the file." : "Video attached — couldn't read its length, please enter the duration.");
      toast.success("Video uploaded");
    } catch (e) { setVidPct(null); toast.error(apiError(e.response?.data?.detail)); }
  };

  const pickThumb = async (file) => {
    setThumbBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      set("thumbnail_url", `/api/files/${data.file_id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setThumbBusy(false); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const body = { title: form.title.trim(), description: form.description, category: form.category, duration: Number(form.duration) || 0, thumbnail_url: form.thumbnail_url, video_url: form.video_url, transcript: form.transcript, featured: form.featured, status: form.status };
      if (lesson?.id) await api.put(`/admin/academy/lessons/${lesson.id}`, body); else await api.post("/admin/academy/lessons", body);
      toast.success("Lesson saved"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploading = vidPct != null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!uploading) onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="academy-lesson-dialog">
        <DialogHeader><DialogTitle>{lesson?.id ? "Edit lesson" : "New lesson"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="academy-form-title" /></div>
          <div className="sm:col-span-2 space-y-1.5"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="academy-form-description" /></div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="academy-form-category"><SelectValue /></SelectTrigger>
              <SelectContent>{ACADEMY_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Duration (minutes)</Label><Input type="number" min="0" step="0.5" value={Math.round(((form.duration || 0) / 60) * 10) / 10} onChange={(e) => set("duration", Math.round(Number(e.target.value || 0) * 60))} data-testid="academy-form-duration" /></div>
          <MediaPicker label="Video" icon={Film} accept="video/mp4,video/webm,video/quicktime,video/x-m4v" progress={vidPct} busy={uploading} onPick={pickVideo} testid="academy-form-video-input">
            <div className="text-xs text-muted-foreground" data-testid="academy-form-video-status">{form.video_url ? (vidMsg || "Video attached.") : "No video yet — partners see “Video coming soon”."}</div>
            {form.video_url && <button type="button" className="text-xs text-destructive underline" onClick={() => { set("video_url", null); setVidPct(null); }} data-testid="academy-form-remove-video">Remove video</button>}
          </MediaPicker>
          <MediaPicker label="Thumbnail" icon={ImageIcon} accept="image/png,image/jpeg,image/webp" busy={thumbBusy} onPick={pickThumb} testid="academy-form-thumb-input">
            {form.thumbnail_url ? <img src={mediaUrl(form.thumbnail_url)} alt="" className="w-full aspect-video object-cover rounded-md" data-testid="academy-form-thumb-preview" /> : <div className="text-xs text-muted-foreground">No thumbnail</div>}
          </MediaPicker>
          <div className="sm:col-span-2 space-y-1.5"><Label>Transcript</Label><Textarea rows={5} value={form.transcript} onChange={(e) => set("transcript", e.target.value)} placeholder="Paste the video transcript (optional)" data-testid="academy-form-transcript" /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="academy-form-featured" />Featured welcome video</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.status === "archived"} onCheckedChange={(v) => set("status", v ? "archived" : "active")} data-testid="academy-form-archived" />Archived (hidden from partners)</label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={uploading} data-testid="academy-form-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || uploading} data-testid="academy-form-save">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save lesson</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
